import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ActionType, ACTION_LABELS, AutomationConfigForm } from "./AutomationConfigForm";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface StageOption { id: string; stage_name: string }

export interface AutomationRow {
  id?: string;
  workflow_id: string;
  stage_id: string;
  action_type: ActionType;
  action_config: Record<string, any>;
  is_active: boolean;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workflowId: string;
  stages: StageOption[];
  initial?: AutomationRow | null;
  onSaved: () => void;
}

export function AutomationDialog({ open, onOpenChange, workflowId, stages, initial, onSaved }: Props) {
  const [stageId, setStageId] = useState("");
  const [actionType, setActionType] = useState<ActionType>("send_email");
  const [config, setConfig] = useState<Record<string, any>>({});
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initial) {
      setStageId(initial.stage_id);
      setActionType(initial.action_type);
      setConfig(initial.action_config || {});
      setIsActive(initial.is_active);
    } else {
      setStageId(stages[0]?.id || "");
      setActionType("send_email");
      setConfig({});
      setIsActive(true);
    }
  }, [initial, stages, open]);

  const handleSave = async () => {
    if (!stageId) { toast.error("Pick a stage"); return; }
    setSaving(true);
    const payload = {
      workflow_id: workflowId,
      stage_id: stageId,
      action_type: actionType,
      action_config: config,
      is_active: isActive,
    };
    const { error } = initial?.id
      ? await supabase.from("workflow_automations").update(payload).eq("id", initial.id)
      : await supabase.from("workflow_automations").insert(payload);
    setSaving(false);
    if (error) { toast.error("Failed to save automation"); return; }
    toast.success(initial ? "Automation updated" : "Automation added");
    onSaved();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initial ? "Edit automation" : "New automation"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>When job enters stage</Label>
            <Select value={stageId} onValueChange={setStageId}>
              <SelectTrigger><SelectValue placeholder="Select stage" /></SelectTrigger>
              <SelectContent>
                {stages.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.stage_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Then do</Label>
            <Select value={actionType} onValueChange={(v) => { setActionType(v as ActionType); setConfig({}); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {(Object.keys(ACTION_LABELS) as ActionType[]).map((k) => (
                  <SelectItem key={k} value={k}>{ACTION_LABELS[k]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <AutomationConfigForm
            actionType={actionType}
            config={config}
            onChange={setConfig}
            stageName={stages.find((s) => s.id === stageId)?.stage_name}
          />
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <p className="font-medium text-sm">Active</p>
              <p className="text-xs text-muted-foreground">Inactive rules don't fire</p>
            </div>
            <Switch checked={isActive} onCheckedChange={setIsActive} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
